import { Alert, AlertTitle, Box, Collapse, Stack, Typography, useTheme } from '@mui/material';
import { useStripe } from '@stripe/react-stripe-js';
import { useFormikContext } from 'formik';
import { get, omit } from 'lodash';
import { useState } from 'react';
import { CheckCircle, ChevronDown, ChevronUp, Lock } from 'react-feather';
import { useTranslation } from 'react-i18next';

//Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import IconFeather from 'src/components/common/Icon';

// Utilities
import VisaMasterCard from 'src/asset/images/checkout/visa-master-card.png';
import BankTransfer from 'src/asset/images/checkout/bank-transfer.png';

import { CheckoutStep } from 'src/features/Checkout/config';
import { useNotification } from 'src/hooks/useNotification';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useMutateGetCheckoutSession, useMutateUpdateCustomerInfo } from 'src/services/mutate';
import { PaymentMethod } from 'src/utils/constants/enum';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { IBillingDetailPayload, ICheckoutForm } from 'src/utils/typings/dto/payment';
import { IPlanDetail } from 'src/utils/typings/response';
import { useNavigate } from 'react-router-dom';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';

const PaymentMethodTab = ({
  open,
  planDetail,
  campaignSlug = '',
  handleChangeStep,
}: {
  open: boolean;
  planDetail: IPlanDetail;
  campaignSlug?: string;
  handleChangeStep: (step: CheckoutStep) => void;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { error_code, planId } = useQueryParams();
  const { t } = useTranslation();
  const { notification } = useNotification();
  const formik = useFormikContext<ICheckoutForm>();
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(PaymentMethod.CreditCard);

  const { mutateAsync } = useMutateUpdateCustomerInfo();
  const { mutateAsync: mutateAsyncCheckoutSession } = useMutateGetCheckoutSession();

  const confirmCheckout = async () => {
    setLoading(true);
    const params: IBillingDetailPayload = {
      ...omit(formik.values, ['plan_type']),
    };

    // Update Customer Billing
    const responseUpdateCustomer = await mutateAsync(params);
    const customerId = responseUpdateCustomer?.data?.customer_id || '';
    switch (selectedMethod) {
      case PaymentMethod.CreditCard:
        payByCreditCard(customerId);
        break;
      default:
        payByTransfer(customerId);
        break;
    }
    setLoading(false);
  };

  const payByCreditCard = async (customerId: string) => {
    const priceId = get(planDetail, `stripe_price_id_${formik.values.plan_type}`);
    const windowVar: ExpectedAny = window;

    // Get Checkout sessionId
    const resultCheckoutSession = await mutateAsyncCheckoutSession({
      price_id: priceId,
      stripe_customer_id: customerId || '',
      client_reference_id: windowVar?.affiliateId || '',
      plan_id: planId,
      campaign_slug: campaignSlug,
    });
    notification(resultCheckoutSession);
    if (resultCheckoutSession.status_code === SUCCESS_CODE.GET) {
      stripe?.redirectToCheckout({
        sessionId: resultCheckoutSession.data?.sessionId || '',
      });
    }
  };

  const payByTransfer = async (customerId: string) => {
    const priceId = get(planDetail, `stripe_price_id_${formik.values.plan_type}`);
    const params = handleQueryParamFromObject({
      priceId,
      planId,
      customerId: customerId || '',
      campaignSlug,
      type: formik.values.plan_type,
    });
    navigate({
      pathname: AuthRouteKeys.BankTransfer,
      search: params,
    });
  };

  return (
    <Stack direction="column">
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box borderRadius={999} border={`1px solid #000`} py={1} px={2}>
          <Typography variant="body1" component="span">
            3
          </Typography>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flex={1}
          mb={3}
          sx={{ cursor: 'pointer' }}
          onClick={() => handleChangeStep(CheckoutStep.PaymentMethod)}
        >
          <Typography variant="h6">{t('checkout.payment_method.title')}</Typography>
          <Stack direction="row" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconFeather icon={<Lock />} muiProps={{ fontSize: 'medium', color: open ? 'primary' : 'disabled' }} />
              <Typography variant="body2">{t('checkout.payment_method.encrypted_secure')}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconFeather
                icon={<CheckCircle />}
                muiProps={{ fontSize: 'medium', color: open ? 'primary' : 'disabled' }}
              />
              <Typography variant="body2">{t('checkout.payment_method.guarantee')}</Typography>
            </Stack>
            <IconFeather icon={open ? <ChevronUp /> : <ChevronDown />} />
          </Stack>
        </Stack>
      </Stack>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack direction="column" spacing={3} pt={3} ml={7} mt={3} borderTop={`1px dashed ${theme.palette.divider}`}>
          {error_code && (
            <Alert severity="error">
              <AlertTitle>{t('checkout.payment_failed.title')}</AlertTitle>
              {t('checkout.payment_failed.description')}
            </Alert>
          )}
          <Typography variant="subtitle2" textTransform="uppercase">
            {t('checkout.payment_method.payment_information')}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Stack
              direction="column"
              alignItems="center"
              borderRadius={theme.shape.borderRadius / 2}
              border={
                selectedMethod === PaymentMethod.CreditCard
                  ? `1px solid ${theme.palette.primary.main}`
                  : `1px solid ${theme.palette.divider}`
              }
              flex={1}
              padding={1.5}
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                setSelectedMethod(PaymentMethod.CreditCard);
              }}
            >
              <Box component="img" src={VisaMasterCard} height={24} width={164} />
              <Typography variant="body1" align="center">
                {t('checkout.payment_method.credit_card')}
              </Typography>
            </Stack>
            <Stack
              direction="column"
              alignItems="center"
              borderRadius={theme.shape.borderRadius / 2}
              border={
                selectedMethod === PaymentMethod.Transfer
                  ? `1px solid ${theme.palette.primary.main}`
                  : `1px solid ${theme.palette.divider}`
              }
              flex={1}
              padding={1.5}
              sx={{ cursor: planDetail?.is_freemium ? '' : 'pointer', opacity: planDetail?.is_freemium ? 0.4 : 1 }}
              onClick={() => {
                !planDetail?.is_freemium && setSelectedMethod(PaymentMethod.Transfer);
              }}
            >
              <Box component="img" src={BankTransfer} height={24} />
              <Typography variant="body1" align="center">
                {t('checkout.payment_method.transfer')}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" pt={2}>
            <ButtonWithLoading
              variant="contained"
              size="large"
              onClick={() => confirmCheckout()}
              disabled={loading}
              isLoading={loading}
            >
              {t('checkout.payment_method.checkout')}
            </ButtonWithLoading>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default PaymentMethodTab;
